// ============================================================
// SecaoFichasNucleo.js — Administração: fichas de cadastro de núcleo
//
// A aba por onde o administrador conduz a abertura de um núcleo de ponta a
// ponta: abre a ficha, manda um link para a coordenação geral e outro para
// a coordenação técnica, acompanha o que já chegou, confere e cria o
// núcleo a partir do que foi preenchido.
//
// Só o administrador vê esta tela (controlado no App.js) e o servidor
// recusa de novo, porque esconder o botão não é permissão. Criar núcleo é
// decisão de programa: envolve convênio, instituição responsável e o termo
// de consentimento que sai com o nome dela.
//
// Enviar a ficha NÃO cria nada. O identificador do núcleo é chave
// estrangeira em cinco coleções e não muda depois, então quem confirma é
// quem responde pelo programa, com a ficha na frente.
// ============================================================

import { useState, useEffect, useCallback } from "react";
import {
  criarFichaDeNucleo, listarFichasDeNucleo, atualizarFichaDeNucleo,
  criarNucleoDaFicha, linkDaFicha,
} from "../firebase";
import { useNucleos } from "../NucleosContexto";
import { Card, Btn, Input, StatusBox } from "../componentes";
import {
  AZUL, TEXTO, CINZA_ESC, VERDE_LOGO, PERIGO, ALERTA, BORDA, FUNDO_SUAVE, RAIO,
} from "../tema";

const PREFIXO_ID = "RT_";

const DIACRITICOS = new RegExp(
  "[" + String.fromCharCode(0x300) + "-" + String.fromCharCode(0x36f) + "]",
  "g"
);

// O mesmo ajuste da tela de núcleos: o identificador não aceita espaço nem
// acento, porque entra no ID dos usuários da coordenação.
function sufixoId(texto) {
  return String(texto || "")
    .normalize("NFD").replace(DIACRITICOS, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

const CORES_DO_ESTADO = {
  aguardando: { fundo: "#F1F5F9", cor: CINZA_ESC, texto: "Aguardando as duas partes" },
  parcial: { fundo: "#FEF3C7", cor: ALERTA, texto: "Falta uma parte" },
  completa: { fundo: "#DCFCE7", cor: "#047857", texto: "Completa" },
  usada: { fundo: "#EAF0FA", cor: AZUL, texto: "Núcleo criado" },
  cancelada: { fundo: "#FEE2E2", cor: PERIGO, texto: "Cancelada" },
};

function Etiqueta({ estado }) {
  const c = CORES_DO_ESTADO[estado] || CORES_DO_ESTADO.aguardando;
  return (
    <span style={{
      background: c.fundo, color: c.cor, borderRadius: 999,
      padding: "4px 11px", fontSize: 11.5, fontWeight: 800, whiteSpace: "nowrap",
    }}>{c.texto}</span>
  );
}

function dataCurta(valor) {
  if (!valor) return "";
  // O Firestore chega como { _seconds } pelo callable, e como string no
  // que foi serializado. As duas formas caem aqui.
  const ms = valor._seconds ? valor._seconds * 1000
    : valor.seconds ? valor.seconds * 1000
      : Date.parse(valor);
  if (!ms || isNaN(ms)) return "";
  return new Date(ms).toLocaleDateString("pt-BR");
}

// O link de cada parte, com o botão de copiar. É a única coisa que sai
// desta tela para fora do sistema, então aparece inteiro: quem vai colar
// numa mensagem precisa ver o que está colando.
function LinhaDoLink({ titulo, para, link, chegouEm, corrigidaEm, onRegerar }) {
  const [copiado, setCopiado] = useState(false);

  async function copiar() {
    try {
      await navigator.clipboard.writeText(link);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      // Navegador que recusa a área de transferência sem gesto reconhecido,
      // ou sem permissão. O link está na tela e dá para selecionar à mão.
      setCopiado(false);
    }
  }

  return (
    <div style={{ background: FUNDO_SUAVE, border: `1px solid ${BORDA}`, borderRadius: RAIO.md, padding: "11px 13px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: TEXTO }}>{titulo}</span>
        <span style={{ fontSize: 11.5, color: chegouEm ? VERDE_LOGO : CINZA_ESC, fontWeight: 600 }}>
          {chegouEm
            ? `Recebida em ${dataCurta(chegouEm)}${corrigidaEm ? `, corrigida em ${dataCurta(corrigidaEm)}` : ""}`
            : "Ainda não chegou"}
        </span>
      </div>
      {para && <div style={{ fontSize: 12, color: CINZA_ESC, marginTop: 3 }}>Para: {para}</div>}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
        <input
          readOnly value={link} onFocus={(e) => e.target.select()}
          style={{
            flex: 1, minWidth: 220, border: `1px solid ${BORDA}`, borderRadius: 7,
            padding: "7px 9px", fontSize: 12, fontFamily: "monospace",
            color: CINZA_ESC, background: "#FFF", boxSizing: "border-box",
          }}
        />
        <Btn tamanho="sm" variant={copiado ? "discreto" : "secondary"} onClick={copiar}>
          {copiado ? "Copiado" : "Copiar"}
        </Btn>
        <Btn tamanho="sm" variant="discreto" onClick={onRegerar}
          titulo="Gera um link novo e invalida este, para o caso de ele ter ido para a pessoa errada">
          Gerar outro
        </Btn>
      </div>
    </div>
  );
}

// O que uma parte trouxe, em texto corrido. Mostrar o que chegou é o
// trabalho desta tela: o administrador confere antes de criar, e o que
// está em branco é o que ele vai cobrar.
function Campo({ rotulo, valor }) {
  const vazio = valor === null || valor === undefined || valor === "" ||
    (Array.isArray(valor) && valor.length === 0);
  return (
    <div style={{ display: "flex", gap: 8, fontSize: 12.5, lineHeight: 1.65, flexWrap: "wrap" }}>
      <span style={{ color: CINZA_ESC, minWidth: 190 }}>{rotulo}</span>
      <span style={{ color: vazio ? ALERTA : TEXTO, fontWeight: vazio ? 600 : 500, flex: 1, minWidth: 180 }}>
        {vazio ? "não informado" : (Array.isArray(valor) ? valor.join(", ") : String(valor))}
      </span>
    </div>
  );
}

function ParteGeralRecebida({ parte }) {
  if (!parte || !parte.recebidaEm) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <Campo rotulo="Nome do núcleo" valor={parte.nomeDoNucleo} />
      <Campo rotulo="Estado" valor={parte.estado} />
      <Campo rotulo="Sigla da instituição" valor={parte.siglaInstituicao} />
      <Campo rotulo="Instituição responsável" valor={parte.instituicao} />
      <Campo rotulo="Canal de direitos" valor={parte.canalDeDireitos} />
      <Campo rotulo="Coordenador do núcleo" valor={parte.coordenadorNome} />
      <Campo rotulo="E-mail do coordenador" valor={parte.coordenadorEmail} />
      <Campo rotulo="Telefone do coordenador" valor={parte.coordenadorTelefone} />
      <Campo rotulo="Coordenador técnico" valor={parte.coordenadorTecnicoNome} />
      <Campo rotulo="E-mail do coordenador técnico" valor={parte.coordenadorTecnicoEmail} />
      <Campo rotulo="Modalidades" valor={parte.modalidades} />
      <Campo rotulo="Observações" valor={parte.observacoes} />
      <Campo rotulo="Quem preencheu" valor={[parte.quemPreencheuNome, parte.quemPreencheuEmail].filter(Boolean).join(" · ")} />
    </div>
  );
}

function ParteTecnicaRecebida({ parte }) {
  if (!parte || !parte.recebidaEm) return null;
  const categorias = parte.categoriasPorModalidade || {};
  const locais = parte.locais || [];
  const equipe = parte.equipe || [];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <Campo rotulo="Modalidades informadas aqui" valor={parte.modalidades} />
      {Object.keys(categorias).length === 0
        ? <Campo rotulo="Categorias" valor="" />
        : Object.entries(categorias).map(([k, lista]) => (
          <Campo key={k} rotulo={`Categorias de ${k}`} valor={lista} />
        ))}
      <Campo rotulo="Locais de treino" valor={locais.length ? `${locais.length} local(is)` : ""} />
      {locais.map((l, i) => (
        <div key={i} style={{ fontSize: 12.5, color: TEXTO, paddingLeft: 198, lineHeight: 1.65 }}>
          {l.nome || "(sem nome)"}
          {l.endereco ? ` · ${l.endereco}` : ""}
          {Number.isFinite(l.lat) && Number.isFinite(l.lng)
            ? ` · ${l.lat}, ${l.lng}`
            : <span style={{ color: ALERTA, fontWeight: 600 }}> · sem ponto no mapa</span>}
        </div>
      ))}
      <Campo rotulo="Equipe indicada" valor={equipe.length ? `${equipe.length} pessoa(s)` : ""} />
      {equipe.map((p, i) => (
        <div key={i} style={{ fontSize: 12.5, color: TEXTO, paddingLeft: 198, lineHeight: 1.65 }}>
          {[p.nome, p.perfil, p.modalidade, p.email].filter(Boolean).join(" · ")}
        </div>
      ))}
      <Campo rotulo="Observações" valor={parte.observacoes} />
      <Campo rotulo="Quem preencheu" valor={[parte.quemPreencheuNome, parte.quemPreencheuEmail].filter(Boolean).join(" · ")} />
    </div>
  );
}

export default function SecaoFichasNucleo() {
  const [fichas, setFichas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [mensagem, setMensagem] = useState(null);

  // Abertura de ficha
  const [rotulo, setRotulo] = useState("");
  const [sufixo, setSufixo] = useState("");
  const [recado, setRecado] = useState("");
  const [paraGeral, setParaGeral] = useState("");
  const [paraTecnico, setParaTecnico] = useState("");
  const [abrindo, setAbrindo] = useState(false);

  // Conferência e criação, uma ficha por vez
  const [abertaId, setAbertaId] = useState(null);
  const [idParaCriar, setIdParaCriar] = useState("");
  const [nomeParaCriar, setNomeParaCriar] = useState("");
  const [criando, setCriando] = useState(false);
  const { recarregarNucleos } = useNucleos();

  const recarregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await listarFichasDeNucleo();
      setFichas(r.fichas || []);
    } catch (e) {
      setMensagem({ tipo: "erro", msg: "Erro ao carregar as fichas: " + (e.message || "") });
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { recarregar(); }, [recarregar]);

  async function abrirFicha() {
    if (!rotulo.trim()) {
      setMensagem({ tipo: "erro", msg: "Dê um nome de referência à ficha, para você reconhecê-la na lista." });
      return;
    }
    setAbrindo(true);
    setMensagem(null);
    try {
      const r = await criarFichaDeNucleo({
        rotulo: rotulo.trim(),
        nucleoIdPretendido: sufixo.trim() ? PREFIXO_ID + sufixoId(sufixo) : "",
        recado: recado.trim(),
        destinatarioGeral: paraGeral.trim(),
        destinatarioTecnico: paraTecnico.trim(),
      });
      setMensagem({
        tipo: "ok",
        msg: `Ficha aberta. Copie os dois links abaixo e mande cada um para o seu coordenador. Eles valem por ${r.diasDeValidade} dias.`,
      });
      setRotulo(""); setSufixo(""); setRecado(""); setParaGeral(""); setParaTecnico("");
      await recarregar();
      setAbertaId(r.fichaId);
    } catch (e) {
      setMensagem({ tipo: "erro", msg: e.message || "Erro ao abrir a ficha." });
    } finally {
      setAbrindo(false);
    }
  }

  async function acaoNaFicha(fichaId, acao, parte) {
    setMensagem(null);
    try {
      const r = await atualizarFichaDeNucleo({ fichaId, acao, parte });
      setMensagem({ tipo: "ok", msg: r.mensagem });
      await recarregar();
    } catch (e) {
      setMensagem({ tipo: "erro", msg: e.message || "Erro ao atualizar a ficha." });
    }
  }

  function abrirConferencia(ficha) {
    setAbertaId(ficha.id === abertaId ? null : ficha.id);
    const d = ficha.dadosParaCriar || {};
    setIdParaCriar(d.nucleoId || ficha.nucleoIdPretendido || "");
    setNomeParaCriar(d.nome || "");
    setMensagem(null);
  }

  async function criarONucleo(ficha) {
    const nucleoId = idParaCriar.trim().toUpperCase();
    if (!nucleoId) {
      setMensagem({ tipo: "erro", msg: "Informe o identificador do núcleo antes de criar." });
      return;
    }
    setCriando(true);
    setMensagem(null);
    try {
      const r = await criarNucleoDaFicha(ficha.id, {
        nucleoId,
        nome: nomeParaCriar.trim(),
      });
      const equipe = r.equipeSugerida || [];
      const fora = r.locaisDeFora || [];
      const partes = [r.mensagem || `Núcleo ${nucleoId} criado.`];
      if (equipe.length) {
        partes.push(`Agora cadastre a equipe em Gestão, Gerenciar usuários: ${equipe.map((p) => p.nome || p.email).filter(Boolean).join(", ")}.`);
      }
      if (fora.length) {
        partes.push(`Ficaram de fora ${fora.length} local(is) da ficha, por falta de nome ou de ponto no mapa: ${fora.map((l) => l.nome).join(", ")}. Cadastre em Núcleos, Editar.`);
      }
      setMensagem({ tipo: "ok", msg: partes.join(" ") });
      setAbertaId(null);
      await recarregar();
      // O seletor global de núcleo e as telas que leem modalidades saem do
      // mesmo contexto. Sem isto, o núcleo recém-criado só apareceria no
      // próximo carregamento do painel.
      await recarregarNucleos();
    } catch (e) {
      setMensagem({ tipo: "erro", msg: e.message || "Erro ao criar o núcleo." });
    } finally {
      setCriando(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <Card>
        <div style={{ fontSize: 16, fontWeight: 800, color: TEXTO, marginBottom: 4 }}>
          Abrir ficha de cadastro de núcleo
        </div>
        <div style={{ fontSize: 13, color: CINZA_ESC, marginBottom: 16, lineHeight: 1.65 }}>
          A ficha é o levantamento que antecede a criação do núcleo. Ela gera dois
          links: um para a <b>coordenação geral</b>, que informa a instituição
          responsável, o canal de direitos e quem vai coordenar, e um para a{" "}
          <b>coordenação técnica</b>, que informa categorias, locais de treino e
          equipe. Cada link escreve só na sua parte, então um não apaga o outro, e
          você vê qual das duas ainda falta. Preencher não cria o núcleo: você
          confere e cria.
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Input label="Nome de referência (só para você)" value={rotulo} onChange={setRotulo}
              placeholder="Ex: CFO Ceará, abertura 2027" style={{ flex: 1, minWidth: 260 }} />
            <div style={{ display: "flex", flexDirection: "column", gap: 5, flex: 1, minWidth: 220 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: CINZA_ESC }}>
                Identificador pretendido (opcional)
              </label>
              <div style={{ display: "flex", alignItems: "stretch" }}>
                <span style={{
                  border: `1px solid ${BORDA}`, borderRight: "none", borderRadius: "7px 0 0 7px",
                  padding: "9px 10px", fontSize: 14, fontWeight: 800, color: AZUL, background: "#F1F5F9",
                }}>{PREFIXO_ID}</span>
                <input value={sufixo} onChange={(e) => setSufixo(e.target.value)}
                  placeholder="CEARA  ou  CFO_CEARA"
                  style={{
                    flex: 1, border: `1px solid ${BORDA}`, borderRadius: "0 7px 7px 0",
                    padding: "9px 12px", fontSize: 14, color: TEXTO, fontFamily: "inherit",
                    boxSizing: "border-box", minWidth: 0,
                  }} />
              </div>
              <div style={{ fontSize: 12, color: sufixo ? VERDE_LOGO : CINZA_ESC }}>
                {sufixo
                  ? `Ficará: ${PREFIXO_ID}${sufixoId(sufixo)}`
                  : "Pode decidir depois, quando a ficha voltar com o estado e a instituição."}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Input label="Link da parte geral vai para" value={paraGeral} onChange={setParaGeral}
              placeholder="Nome ou e-mail do coordenador geral" style={{ flex: 1, minWidth: 240 }} />
            <Input label="Link da parte técnica vai para" value={paraTecnico} onChange={setParaTecnico}
              placeholder="Nome ou e-mail do coordenador técnico" style={{ flex: 1, minWidth: 240 }} />
          </div>

          <Input label="Recado que aparece no topo do formulário (opcional)" value={recado} onChange={setRecado}
            placeholder="Ex: precisamos das coordenadas das pistas até o fim do mês" />

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Btn onClick={abrirFicha} disabled={abrindo}>
              {abrindo ? "Abrindo..." : "Abrir ficha e gerar os links"}
            </Btn>
          </div>
        </div>
      </Card>

      {mensagem && <StatusBox tipo={mensagem.tipo} msg={mensagem.msg} />}

      <Card>
        <div style={{ fontSize: 16, fontWeight: 800, color: TEXTO, marginBottom: 12 }}>
          Fichas
        </div>

        {carregando && <div style={{ fontSize: 13, color: CINZA_ESC }}>Carregando...</div>}
        {!carregando && fichas.length === 0 && (
          <div style={{ fontSize: 13, color: CINZA_ESC }}>
            Nenhuma ficha aberta ainda. A primeira que você abrir aparece aqui com os
            dois links.
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {fichas.map((f) => {
            const aberta = abertaId === f.id;
            const geral = f.parteGeral || {};
            const tecnica = f.parteTecnica || {};
            const podeCriar = f.estado === "completa" || f.estado === "parcial";
            return (
              <div key={f.id} style={{ border: `1px solid ${BORDA}`, borderRadius: RAIO.lg, padding: "14px 16px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: TEXTO }}>
                      {f.rotulo || "(sem nome)"}
                    </div>
                    <div style={{ fontSize: 12, color: CINZA_ESC, marginTop: 3, lineHeight: 1.6 }}>
                      {f.nucleoIdPretendido ? `${f.nucleoIdPretendido} · ` : ""}
                      aberta em {dataCurta(f.criadoEm)}
                      {f.validaAte ? ` · vale até ${dataCurta(f.validaAte)}` : ""}
                      {f.nucleoCriado ? ` · núcleo ${f.nucleoCriado}` : ""}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <Etiqueta estado={f.vencida && f.estado !== "usada" ? "cancelada" : f.estado} />
                    <Btn tamanho="sm" variant="discreto" onClick={() => abrirConferencia(f)}>
                      {aberta ? "Fechar" : "Abrir"}
                    </Btn>
                  </div>
                </div>

                {f.vencida && f.estado !== "usada" && !f.canceladaEm && (
                  <div style={{ fontSize: 12.5, color: ALERTA, fontWeight: 600, marginTop: 8, lineHeight: 1.6 }}>
                    O prazo terminou e os links pararam de funcionar. Renove para
                    reabrir o preenchimento.
                  </div>
                )}

                {aberta && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 16 }}>
                    {f.estado !== "usada" && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <LinhaDoLink
                          titulo="Parte 1, coordenação geral"
                          para={f.destinatarioGeral}
                          link={linkDaFicha(f.id, f.tokenGeral)}
                          chegouEm={geral.recebidaEm}
                          corrigidaEm={geral.corrigidaEm}
                          onRegerar={() => acaoNaFicha(f.id, "regerar", "geral")}
                        />
                        <LinhaDoLink
                          titulo="Parte 2, coordenação técnica"
                          para={f.destinatarioTecnico}
                          link={linkDaFicha(f.id, f.tokenTecnico)}
                          chegouEm={tecnica.recebidaEm}
                          corrigidaEm={tecnica.corrigidaEm}
                          onRegerar={() => acaoNaFicha(f.id, "regerar", "tecnica")}
                        />
                      </div>
                    )}

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16 }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 800, color: TEXTO, marginBottom: 8 }}>
                          O que a coordenação geral respondeu
                        </div>
                        {geral.recebidaEm
                          ? <ParteGeralRecebida parte={geral} />
                          : <div style={{ fontSize: 12.5, color: CINZA_ESC }}>Nada ainda.</div>}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 800, color: TEXTO, marginBottom: 8 }}>
                          O que a coordenação técnica respondeu
                        </div>
                        {tecnica.recebidaEm
                          ? <ParteTecnicaRecebida parte={tecnica} />
                          : <div style={{ fontSize: 12.5, color: CINZA_ESC }}>Nada ainda.</div>}
                      </div>
                    </div>

                    {f.estado === "usada" ? (
                      <div style={{ fontSize: 12.5, color: AZUL, lineHeight: 1.65, background: "#EAF0FA", borderRadius: RAIO.md, padding: "11px 13px" }}>
                        Esta ficha já criou o núcleo <b>{f.nucleoCriado}</b>. Ela fica
                        guardada porque é o registro de quem informou o quê, e o termo
                        de consentimento nasceu dela. Alterações no núcleo agora são
                        em Núcleos, Editar.
                      </div>
                    ) : (
                      <div style={{ borderTop: `1px solid ${BORDA}`, paddingTop: 14, display: "flex", flexDirection: "column", gap: 12 }}>
                        <div style={{ fontSize: 13, fontWeight: 800, color: TEXTO }}>
                          Criar o núcleo com esta ficha
                        </div>
                        <div style={{ fontSize: 12.5, color: CINZA_ESC, lineHeight: 1.65 }}>
                          O identificador é o código interno e <b>não muda depois</b>:
                          ele fica gravado em todo registro de presença e entra no ID
                          dos usuários da coordenação. O nome por extenso você ajusta
                          quando quiser, em Núcleos.
                          {f.locaisIncompletos && f.locaisIncompletos.length > 0 && (
                            <>
                              {" "}Atenção: {f.locaisIncompletos.length} local(is) da ficha
                              não vão entrar, por falta de nome ou de ponto no mapa
                              ({f.locaisIncompletos.map((l) => `${l.nome}, ${l.motivo}`).join("; ")}).
                            </>
                          )}
                        </div>
                        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                          <Input label="Identificador" value={idParaCriar} onChange={setIdParaCriar}
                            placeholder="RT_CFO_CEARA" style={{ flex: 1, minWidth: 220 }} />
                          <Input label="Nome do núcleo" value={nomeParaCriar} onChange={setNomeParaCriar}
                            placeholder="Ex: Centro de Formação Olímpica do Ceará" style={{ flex: 1, minWidth: 260 }} />
                        </div>
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "space-between" }}>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            {f.canceladaEm ? (
                              <Btn tamanho="sm" variant="discreto" onClick={() => acaoNaFicha(f.id, "reabrir")}>
                                Reabrir a ficha
                              </Btn>
                            ) : (
                              <>
                                <Btn tamanho="sm" variant="discreto" onClick={() => acaoNaFicha(f.id, "renovar")}>
                                  Renovar o prazo
                                </Btn>
                                <Btn tamanho="sm" variant="discreto" onClick={() => acaoNaFicha(f.id, "cancelar")}>
                                  Cancelar a ficha
                                </Btn>
                              </>
                            )}
                          </div>
                          <Btn onClick={() => criarONucleo(f)} disabled={criando || !podeCriar}
                            titulo={podeCriar ? "" : "Nenhuma das duas partes chegou ainda"}>
                            {criando ? "Criando..." : "Criar o núcleo"}
                          </Btn>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
